import Router from 'next/router';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSelector } from 'react-redux';
import { useMount } from 'react-use';

import { ClipType } from '@air/api/types';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useAirModal } from '@air/provider-modal';
import { useIsLoggedIn } from '@air/utils-auth';

import { AnnotationToolSelector } from '~/components/AnnotationsV2/components/AnnotationToolSelector';
import { useAnnotationsToggle } from '~/components/AnnotationsV2/hooks/useAnnotationsToggle';
import { useCapturedCommentTimestamp } from '~/components/AnnotationsV2/hooks/useCapturedCommentTimestamp';
import { useCommentTimeMode } from '~/components/AnnotationsV2/hooks/useCommentTimeMode';
import { useEnableCommentRange } from '~/components/AnnotationsV2/hooks/useEnableCommentRange';
import { useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import { formatCommentTimeLabel } from '~/components/AnnotationsV2/shared/formatCommentTimeLabel';
import { BOUNDING_BOX_TEXT_WIDTH } from '~/components/BoundingBox/constants';
import { DiscussionForm, type DiscussionFormHandle } from '~/components/DiscussionsV2/DiscussionForm';
import { PublicCommentAnonymousSignUpModal } from '~/components/PublicLinkAnonymousSignUpModal/PublicCommentAnonymousSignUpModal';
import { type SSOCallbackData } from '~/components/PublicSSOCallback/utils';
import { ANNOTATION_COMMENT_FORM } from '~/constants/testIDs';
import { useNoPreviewInVisualizer } from '~/hooks/useNoPreviewInVisualizer';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { centralizedBoardAncestorsSelector } from '~/store/centralizedBoard/selectors';
import { centralizedClipTypeSelector, centralizedClipWorkspaceIdSelector } from '~/store/centralizedClip/selectors';
import { useGetDiscussionMentionsMembers } from '~/swr-hooks/discussions/useGetDiscussionMentionsMembers';
import { type SubmitNewDiscussionParams } from '~/swr-hooks/discussions/useSubmitNewDiscussion';
import { isMobileAgent } from '~/utils/PlatformHelpers';
import { useAirSelector } from '~/utils/ReduxUtils';

export type FloatingCommentFormProps = {
  /**
   * The hosting surface's discussion submission — bound by the per-surface wrappers
   * (PrivateFloatingCommentForm / PublicFloatingCommentForm) so this shared body stays
   * surface-blind about which API family creates the discussion.
   */
  onSubmitNewDiscussion: (params: SubmitNewDiscussionParams) => void;
};

export const FloatingCommentForm = memo(({ onSubmitNewDiscussion }: FloatingCommentFormProps) => {
  const inputRef = useRef<DiscussionFormHandle>(null);
  const boardId = useURLBoardIdSelector();
  const boardAncestors = useAirSelector(centralizedBoardAncestorsSelector);
  const clipWorkspaceId = useSelector(centralizedClipWorkspaceIdSelector) ?? '';
  const clipType = useSelector(centralizedClipTypeSelector);
  const isVideo = clipType === ClipType.video;
  const { noPreviewInVisualizer } = useNoPreviewInVisualizer();

  const { mentionMembers, isLoadingMentions } = useGetDiscussionMentionsMembers({
    boardId,
    ancestors: boardAncestors,
    workspaceId: clipWorkspaceId,
  });

  const annotations = useAnnotationV2Store((state) => state.annotations);
  const setAnnotations = useAnnotationV2Store((state) => state.setAnnotations);
  const commentRange = useAnnotationV2Store((state) => state.commentRange);
  const setCommentRange = useAnnotationV2Store((state) => state.setCommentRange);
  const setCommentRangeEnabled = useAnnotationV2Store((state) => state.setCommentRangeEnabled);
  const shouldEnableTimestamps = useAnnotationV2Store((state) => state.shouldEnableCommentTimestamps);
  const setShouldEnableCommentTimestamps = useAnnotationV2Store((state) => state.setShouldEnableCommentTimestamps);
  const registerCommentInputFocusHandler = useAnnotationV2Store((state) => state.registerCommentInputFocusHandler);
  const { hideAnnotations, isAnnotationsEnabled } = useAnnotationsToggle();
  const { capturedTimestamp } = useCapturedCommentTimestamp({ enabled: shouldEnableTimestamps });
  const { enableCommentRange } = useEnableCommentRange();

  const { isAboveMediumScreen } = useBreakpointsContext();
  const canSelectTimeRange = isVideo && isAboveMediumScreen && !isMobileAgent;

  const { isLoggedIn } = useIsLoggedIn();
  const [showSignUpModal] = useAirModal(PublicCommentAnonymousSignUpModal);

  useMount(() => {
    inputRef.current?.focus();
  });

  useEffect(
    () =>
      registerCommentInputFocusHandler('annotation', () => {
        inputRef.current?.focus();
      }),
    [registerCommentInputFocusHandler],
  );

  // Clear all annotation state
  const clearAllAnnotationState = useCallback(() => {
    setAnnotations([]);
  }, [setAnnotations]);

  const clearSubmittedAnnotationState = useCallback(() => {
    clearAllAnnotationState();

    if (isAnnotationsEnabled) {
      hideAnnotations();
    }
  }, [clearAllAnnotationState, hideAnnotations, isAnnotationsEnabled]);

  const clearRangeState = useCallback(() => {
    setCommentRange(null);
    setCommentRangeEnabled(false);
  }, [setCommentRange, setCommentRangeEnabled]);

  const handleCancel = useCallback(() => {
    clearAllAnnotationState();
  }, [clearAllAnnotationState]);

  const handleEnableTimestampsClick = useCallback(
    (enable: boolean) => {
      setShouldEnableCommentTimestamps(enable);

      if (!enable) {
        clearRangeState();
      }
    },
    [clearRangeState, setShouldEnableCommentTimestamps],
  );

  const { timeMode, selectTimeMode } = useCommentTimeMode({
    canSelectTimeRange,
    shouldEnableTimestamps,
    setShouldEnableTimestamps: handleEnableTimestampsClick,
  });

  const handleFocus = useCallback(() => {
    if (canSelectTimeRange && shouldEnableTimestamps) {
      enableCommentRange();
    }
  }, [canSelectTimeRange, enableCommentRange, shouldEnableTimestamps]);

  const formattedTimestamp = formatCommentTimeLabel({ commentRange, currentTime: capturedTimestamp });

  useHotkeys(
    'esc',
    () => {
      clearAllAnnotationState();
    },
    { enableOnFormTags: ['TEXTAREA'] },
    [clearAllAnnotationState],
  );

  const submitComment = useCallback(
    (comment: string) => {
      const shouldAttachTime = isVideo && shouldEnableTimestamps;
      const isRange = canSelectTimeRange && !!commentRange && commentRange.startTime !== commentRange.endTime;
      const hasDiscussionContext = annotations.length > 0 || !!commentRange;
      let startTimestamp: number | undefined;

      if (shouldAttachTime && isRange) {
        startTimestamp = Math.min(commentRange.startTime, commentRange.endTime);
      } else if (shouldAttachTime) {
        startTimestamp = canSelectTimeRange ? (commentRange?.startTime ?? capturedTimestamp) : capturedTimestamp;
      }

      const endTimestamp =
        shouldAttachTime && isRange ? Math.max(commentRange.startTime, commentRange.endTime) : undefined;

      const createNewDiscussion = () => {
        if (!hasDiscussionContext) {
          return false;
        }

        onSubmitNewDiscussion({
          newComment: comment,
          timestamp: startTimestamp,
          endTimestamp,
          trackLocation: 'asset-page',
          annotations: annotations.length > 0 ? annotations : undefined,
        });

        return true;
      };

      if (isLoggedIn) {
        const didSubmit = createNewDiscussion();

        if (!didSubmit) {
          return;
        }

        // Clear annotation state and exit annotation mode after successful submission.
        clearSubmittedAnnotationState();
        if (commentRange) {
          clearRangeState();
        }
      } else {
        if (!hasDiscussionContext) {
          return;
        }

        // Use annotations directly for anonymous user flow
        const ssoCallbackData: SSOCallbackData = {
          url: Router.asPath,
          action: {
            action: 'create-public-discussion',
            data: {
              newComment: comment,
              annotations: annotations.length > 0 ? annotations : undefined,
              timestamp: startTimestamp,
              endTimestamp,
              trackLocation: 'asset-page',
            },
          },
        };

        showSignUpModal({
          ssoCallbackData,
          onLoginSuccess: createNewDiscussion,
        });

        // Clear annotation state immediately; the SSO payload already captured the annotation.
        clearSubmittedAnnotationState();
        if (commentRange) {
          clearRangeState();
        }
      }
    },
    [
      annotations,
      clearRangeState,
      clearSubmittedAnnotationState,
      commentRange,
      canSelectTimeRange,
      capturedTimestamp,
      isVideo,
      isLoggedIn,
      shouldEnableTimestamps,
      showSignUpModal,
      onSubmitNewDiscussion,
    ],
  );

  return (
    <div className="mx-3 rounded-8 ads:bg-raised bg-gray-1 p-3 shadow-popover [&>form]:border-none [&>form]:shadow-none [&>form]:focus-within:border-none [&>form]:focus-within:shadow-none">
      <DiscussionForm
        data-testid={ANNOTATION_COMMENT_FORM}
        ref={inputRef}
        onSubmit={submitComment}
        onCancel={handleCancel}
        onFocus={handleFocus}
        isLoadingMentions={isLoadingMentions}
        mentionsList={mentionMembers}
        placeholder="Add a comment"
        leftActions={isAnnotationsEnabled ? <AnnotationToolSelector /> : undefined}
        hasCancelButton={true}
        shouldShowMentionsButton={isAboveMediumScreen}
        style={{ width: BOUNDING_BOX_TEXT_WIDTH }}
        {...(isVideo && {
          shouldEnableTimestamps,
          setShouldEnableTimestamps: handleEnableTimestampsClick,
          formattedTimestamp,
          shouldDisableTimestampsAndAnnotations: noPreviewInVisualizer,
          timeMode,
          onSelectTimeMode: selectTimeMode,
          canSelectTimeRange,
        })}
      />
    </div>
  );
});

FloatingCommentForm.displayName = 'FloatingCommentForm';
